<script setup lang="ts">
// A decorative code constellation for the sign-in page: clusters of modules,
// their dependencies, and the colours the map uses for read, edit and probe.
// Deterministic (seeded) and static; no data, no labels.
interface N { x: number; y: number; r: number; tone: "read" | "edit" | "probe" | "plain" }

function rng(seed: number) {
  return () => ((seed = (seed * 1664525 + 1013904223) % 4294967296) / 4294967296);
}
const rand = rng(20261011);
const hubs = [[120, 110], [330, 70], [250, 200], [440, 190], [80, 230]] as const;
const nodes: N[] = [];
const edges: [number, number][] = [];
hubs.forEach(([hx, hy], h) => {
  const hub = nodes.push({ x: hx, y: hy, r: 5, tone: h === 1 ? "edit" : h === 3 ? "probe" : "read" }) - 1;
  const n = 9 + Math.floor(rand() * 7);
  for (let i = 0; i < n; i++) {
    const a = rand() * Math.PI * 2, d = 18 + rand() * 46;
    const k = nodes.push({ x: hx + Math.cos(a) * d * 1.35, y: hy + Math.sin(a) * d, r: 1.4 + rand() * 1.6,
      tone: rand() > .86 ? (rand() > .5 ? "edit" : "probe") : rand() > .6 ? "read" : "plain" }) - 1;
    edges.push([hub, k]);
    if (rand() > .7 && k > hub + 1) edges.push([k, k - 1]);
  }
  if (h > 0) edges.push([hub, 0]);
});
edges.push([3, nodes.length - 1]);
</script>

<template>
  <svg viewBox="0 0 520 290" role="presentation" aria-hidden="true">
    <g class="edges">
      <line v-for="([a, b], i) in edges" :key="i" :x1="nodes[a].x" :y1="nodes[a].y" :x2="nodes[b].x" :y2="nodes[b].y" :class="{ hub: nodes[a].r === 5 && nodes[b].r === 5 }" />
    </g>
    <circle v-for="(n, i) in nodes" :key="i" :cx="n.x" :cy="n.y" :r="n.r" :class="[n.tone, { glow: n.r === 5 }]" />
    <g class="legend" transform="translate(6 280)">
      <circle cx="3" cy="-3" r="3" class="read" /><text x="10" y="0">Read</text>
      <circle cx="56" cy="-3" r="3" class="edit" /><text x="63" y="0">Edit</text>
      <circle cx="104" cy="-3" r="3" class="probe" /><text x="111" y="0">Probe</text>
    </g>
  </svg>
</template>

<style scoped>
line { stroke: var(--line); stroke-width: .7; }
line.hub { stroke: color-mix(in srgb, var(--brand) 55%, transparent); stroke-width: 1.1; }
circle.plain { fill: var(--faint); opacity: .55; }
circle.read { fill: var(--read); }
circle.edit { fill: var(--edit); }
circle.probe { fill: var(--probe); }
circle.glow { stroke: color-mix(in srgb, currentColor 30%, transparent); stroke-width: 6; stroke-opacity: .2; animation: breathe 4s var(--ease) infinite; }
circle.read.glow { color: var(--read); }
circle.edit.glow { color: var(--edit); }
circle.probe.glow { color: var(--probe); }
text { font: var(--text-xs) var(--font-mono); fill: var(--muted); }
@keyframes breathe { 50% { stroke-opacity: .45; } }
</style>
